import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Row } from 'antd';
import ConditionalRenderingRow from '../../forms/FormBuilder/FormFields/ConditionalRenderingRow';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import SimpleTextInputModal from '../../common/modals/SimpleTextInputModal';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  createSavedABWorkReportFilter,
  deleteSavedABWorkReportFilter,
  updateSavedABWorkReportFilter,
} from '../../abWorkReporting/state/abWorkReporting.actions';

const validFieldTypes = new Set([
  'text',
  'number',
  'date',
  'attribute',
  'calculation',
  'dateTime',
]);

export default function ABWorkReportFilterTab({
  onClose,
  filterView,
  setActiveTab,
  columns,
  filters,
  setFilters,
}) {
  const dispatch = useDispatch();

  const [tempFilters, setTempFilters] = useState(
    filterView ? filterView.settings : filters,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setTempFilters(filterView ? filterView.settings : filters);
  }, [filterView, filters]);

  const sections = useMemo(() => {
    const filterableColumns = columns.filter((column) => (
      validFieldTypes.has(column.type)
      || (column.type === 'dropdown' && column.dropdownOptions?.length > 0)
    ));
    return [{
      id: 'section-1',
      fields: filterableColumns.map((column) => ({
        id: column.dataIndex,
        selectedType: column.type,
        configProps: {
          dataType: 'Custom',
          title: column.textTitle || column.title,
          customOptions: column.dropdownOptions,
        },
      })),
    }];
  }, [columns]);

  const onApply = () => {
    setFilters(tempFilters);
    onClose();
  };

  const onClear = () => {
    setTempFilters([]);
    setFilters([]);
    onClose();
  };

  const onSave = () => {
    if (filterView) {
      dispatch(updateSavedABWorkReportFilter(filterView.id, {
        name: filterView.name,
        settings: tempFilters,
      }));
      onApply();
    } else {
      setIsModalOpen(true);
    }
  };

  const onDelete = () => {
    CustomConfirmModal({
      title: 'Delete Filter',
      content: (
        <p>
          Are you sure you wish to delete this filter?
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        await dispatch(deleteSavedABWorkReportFilter(filterView.id));
        setActiveTab('custom');
      },
    });
  };

  const addNewFilterView = async (name) => {
    const result = await dispatch(createSavedABWorkReportFilter({
      name,
      settings: tempFilters,
    }));
    if (result) {
      setIsModalOpen(false);
      onApply();
    }
  };

  return (
    <>
      <div>
        <ConditionalRenderingRow
          onFormulaChange={setTempFilters}
          hasConditionalRendering
          conditionalRenderingFormula={tempFilters}
          sections={sections}
          showCheckbox={false}
        />
      </div>
      <div className="drawer-footer">
        <Row justify="end" gutter={10}>
          <div style={{ marginRight: 'auto' }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {filterView && (
              <OnTraccrButton
                title="Delete"
                type="back"
                onClick={onDelete}
                style={{ marginLeft: 8 }}
              />
            )}
            {!filterView && (
              <OnTraccrButton
                onClick={onClear}
                title="Clear"
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
          <OnTraccrButton
            title={`${filterView ? 'Save' : 'Create'} and Apply`}
            onClick={onSave}
            style={{ marginRight: 8 }}
          />
          <OnTraccrButton
            title="Apply"
            onClick={onApply}
          />
        </Row>
      </div>
      <SimpleTextInputModal
        title="Save Filter"
        visible={isModalOpen}
        placeholder="Enter filter name"
        onClose={() => setIsModalOpen(false)}
        onSave={addNewFilterView}
      />
    </>
  );
}

ABWorkReportFilterTab.propTypes = {
  onClose: PropTypes.func.isRequired,
  filterView: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  setActiveTab: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
};

ABWorkReportFilterTab.defaultProps = {
  filterView: null,
};
